import { useForm, router } from '@inertiajs/react'
import { useEffect, useRef, useState } from 'react'
import Modal from '../shared/Modal'
import type { User } from '../../types'
import { sortPeople } from '../../utils/peopleSort'
import {
  NO_ELIGIBLE_OWNERS_MESSAGE,
  OWNER_PLACEHOLDER,
} from '../../utils/actionItemOwners'

interface ActionItemModalProps {
  isOpen: boolean
  onClose: () => void
  meetingId?: number
  teamId?: number
  teamMembers: User[]
  defaultTitle?: string
  defaultDescription?: string
  convertTopicId?: number
  convertUrl?: string
  convertExtraData?: Record<string, string | boolean>
  // Fired only when a conversion actually succeeded, so callers can close a
  // still-open topic edit panel on success while leaving it untouched when
  // the conversion is cancelled.
  onConverted?: () => void
  subtleBackdrop?: boolean
}

export default function ActionItemModal({ isOpen, onClose, meetingId, teamId, teamMembers, defaultTitle = '', defaultDescription = '', convertTopicId, convertUrl, convertExtraData, onConverted, subtleBackdrop }: ActionItemModalProps) {
  const defaultDueDate = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  const isConversion = !!convertTopicId || !!convertUrl
  const [submitting, setSubmitting] = useState(false)
  const titleRef = useRef<HTMLInputElement>(null)
  const eligibleOwnerIds = teamMembers
    .map(member => String(member.id))
    .sort()
    .join(',')

  const { data, setData, post, processing, reset, errors, transform, clearErrors } = useForm({
    title: defaultTitle,
    owner_id: '',
    due_date: defaultDueDate,
    description: defaultDescription,
  })

  useEffect(() => {
    if (isOpen) {
      setData({
        title: defaultTitle,
        owner_id: '',
        due_date: defaultDueDate,
        description: defaultDescription,
      })
      clearErrors()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, defaultTitle, defaultDescription])

  useEffect(() => {
    if (!data.owner_id || isOwnerEligible(data.owner_id)) return

    setData(prev => {
      if (!prev.owner_id || isOwnerEligible(prev.owner_id)) return prev
      return { ...prev, owner_id: '' }
    })
    // Owner reconciliation is intentionally narrower than form
    // initialization so heartbeat identity changes do not discard drafts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligibleOwnerIds])

  const titleError = errors.title
  const ownerError = errors.owner_id || (errors as Record<string, string | undefined>).owner
  const dueDateError = errors.due_date
  const baseError = (errors as Record<string, string | undefined>).base
  const hasEligibleOwners = teamMembers.length > 0
  const isOwnerEligible = (ownerId: string) =>
    teamMembers.some(member => String(member.id) === ownerId)
  const ownerIsEligible = isOwnerEligible(data.owner_id)

  const submitCreate = (createAnother: boolean) => {
    if (!data.title.trim() || !data.owner_id || !data.due_date || !isOwnerEligible(data.owner_id)) return

    transform(formData => ({
      action_item: {
        title: formData.title,
        owner_id: formData.owner_id,
        due_date: formData.due_date,
        description: formData.description,
        team_id: teamId ? String(teamId) : '',
        source_type: 'meeting',
        source_id: String(meetingId),
      },
    }))
    post(`/meetings/${meetingId}/action_items`, {
      preserveScroll: true,
      preserveState: true,
      onSuccess: () => {
        if (createAnother) {
          setData(prev => ({ ...prev, title: '', description: '' }))
          clearErrors()
          setTimeout(() => titleRef.current?.focus(), 100)
        } else {
          reset()
          onClose()
        }
      },
    })
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (
      !data.title.trim() ||
      !data.owner_id ||
      !data.due_date ||
      !isOwnerEligible(data.owner_id)
    ) return

    if (isConversion) {
      setSubmitting(true)
      const url = convertUrl ?? `/meetings/${meetingId}/topics/${convertTopicId}/convert_to_action_item`
      router.post(url, {
        title: data.title,
        description: data.description,
        owner_id: data.owner_id,
        due_date: data.due_date,
        ...convertExtraData,
      }, {
        preserveScroll: true,
        onSuccess: () => {
          reset()
          onConverted?.()
          onClose()
          setSubmitting(false)
        },
        onError: () => setSubmitting(false),
        // A concurrent Inertia visit (e.g. the meeting heartbeat) can cancel
        // this one; onSuccess/onError never fire then. Reset on cancel/finish
        // so the button can't stay stuck on "Converting..." — the modal stays
        // open so the user can retry.
        onCancel: () => setSubmitting(false),
        onFinish: () => setSubmitting(false),
      })
    } else {
      submitCreate(false)
    }
  }

  const handleClose = () => {
    reset()
    clearErrors()
    onClose()
  }

  const isProcessing = processing || submitting

  return (
    <Modal isOpen={isOpen} onClose={handleClose} subtleBackdrop={subtleBackdrop} title={isConversion ? 'Convert to Action Item' : 'New Action Item'}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {baseError && <p className="text-sm text-status-off-track" role="alert">{baseError}</p>}
        <div>
          <label className="block text-sm font-medium text-text-primary mb-1 font-sans">
            Title <span className="text-pinnacle-red">*</span>
          </label>
          <input
            ref={titleRef}
            type="text"
            value={data.title}
            onChange={e => setData('title', e.target.value)}
            required
            className="w-full px-3 py-2 border border-stone/30 rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20 focus:border-pinnacle-red"
            placeholder="What needs to be done?"
          />
          {titleError && <p className="text-sm text-status-off-track mt-1">{titleError}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-1 font-sans">
            Owner <span className="text-pinnacle-red">*</span>
          </label>
          <select
            value={data.owner_id}
            onChange={e => setData('owner_id', e.target.value)}
            disabled={!hasEligibleOwners}
            required
            aria-invalid={!!ownerError}
            className={`w-full px-3 py-2 border rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20 focus:border-pinnacle-red ${ownerError ? 'border-status-off-track' : 'border-stone/30'}`}
          >
            <option value="">{OWNER_PLACEHOLDER}</option>
            {sortPeople(teamMembers).map(member => (
              <option key={member.id} value={member.id}>
                {member.full_name}
              </option>
            ))}
          </select>
          {ownerError && <p className="text-sm text-status-off-track mt-1">{ownerError}</p>}
          {!hasEligibleOwners && !ownerError && (
            <p className="text-sm text-text-muted mt-1">{NO_ELIGIBLE_OWNERS_MESSAGE}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-1 font-sans">
            Due Date <span className="text-pinnacle-red">*</span>
          </label>
          <input
            type="date"
            value={data.due_date}
            onChange={e => setData('due_date', e.target.value)}
            required
            className="w-full px-3 py-2 border border-stone/30 rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20 focus:border-pinnacle-red"
          />
          {dueDateError && <p className="text-sm text-status-off-track mt-1">{dueDateError}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-1 font-sans">
            Description
          </label>
          <textarea
            value={data.description}
            onChange={e => setData('description', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-stone/30 rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20 focus:border-pinnacle-red resize-none"
            placeholder="Optional details..."
          />
        </div>

        <div className="flex flex-wrap justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-sm font-semibold text-text-primary hover:bg-stone/10 rounded-button transition-colors font-sans"
          >
            Cancel
          </button>
          {!isConversion && (
            <button
              type="button"
              onClick={() => submitCreate(true)}
              disabled={isProcessing || !data.title.trim() || !data.owner_id || !data.due_date || !ownerIsEligible}
              className="px-4 py-2 text-sm font-semibold text-pinnacle-red border border-pinnacle-red hover:bg-pinnacle-red/5 rounded-button transition-colors disabled:opacity-50 font-sans"
            >
              Save and Create Another
            </button>
          )}
          <button
            type="submit"
            disabled={
              isProcessing ||
              !data.title.trim() ||
              !data.owner_id ||
              !data.due_date ||
              !ownerIsEligible
            }
            className="px-4 py-2 text-sm font-semibold text-white bg-pinnacle-red hover:bg-pinnacle-red-dark rounded-button transition-colors disabled:opacity-50 font-sans"
          >
            {isProcessing ? (isConversion ? 'Converting...' : 'Creating...') : (isConversion ? 'Convert' : 'Create Action Item')}
          </button>
        </div>
      </form>
    </Modal>
  )
}
